import React from 'react';

import {utcToFormat} from '~/src/utilities/helpers/date';
import SpeechBubble from '~/src/components/svg/action/SpeechBubble';
import Link from '~/src/components/link/Link';
import {Hashes} from '~/src/utilities/constants/hash';

import css from './RecentlyCommentedItem.module.scss';
import {getContentUrl} from '~/src/utilities/helpers/link';

export interface Comment {
    authorNickname: string;
    body: string;
    commentId: string;
    contentId: string;
    createdAt: string;
}

export interface ImageFocusPoint {
    x: number;
    y: number;
}

export interface ImageVariant {
    aspectRatio: number;
    focusPoint: ImageFocusPoint;
    width: number;
    height: number;
    maxWidth: number;
    maxHeight: number;
    mediaType: 'image/jpeg';
    src: string;
}

export interface ImageVariants {
    '2000px': ImageVariant;
    '1600px': ImageVariant;
    '1200px': ImageVariant;
    '800px': ImageVariant;
    '400px': ImageVariant;
}

export interface Image {
    id: string;
    name?: string;
    promcolor?: string;
    slugifiedName?: string;
    variants: ImageVariants;
}

export interface Content {
    image: Image;
    titleHeader: string;
    title: string;
    url: string;
}

interface Props {
    comment: Comment;
    content: Content;
    onClick: () => void;
}

const RecentlyCommentedItem = ({comment, content, onClick}: Props) => {
    const dataAvailable = comment.commentId;
    const classnames = dataAvailable ? css.recentlyCommentedItem : css.skeleton;
    const {url} = content;

    const imageComponent = content.image ? (
        <img className={css.image} src={content.image.variants['400px'].src} width={70} height={44} />
    ) : (
        <div data-testid="img-fallback" className={css.imgFallback} />
    );

    return (
        <section className={classnames}>
            <span className={css.timestamp}>{comment.createdAt && utcToFormat(comment.createdAt, 'HH:mm')}</span>
            <section className={css.contentSection}>
                <Link href={url && getContentUrl(url, Hashes.COMMENT_SECTION)} className={css.commentContainer} onClick={onClick}>
                    <span className={css.author}>{comment.authorNickname}</span>
                    <div className={css.comment}>
                        {comment.body && (
                            <SpeechBubble primaryClassName={css.primaryColor} secondaryClassName={css.secondaryColor} tertiaryClassName={css.tertiaryColor} />
                        )}
                        <span className={css.commentBody}>{comment.body}</span>
                    </div>
                </Link>
                <Link href={url && getContentUrl(url)} className={css.header} onClick={onClick}>
                    {dataAvailable ? imageComponent : <div data-testid="img-skeleton" className={css.imgSkeleton} />}
                    <div className={css.headlines}>
                        <span className={css.titleHeader}>{content.titleHeader}</span>
                        <h3 className={css.title}>{content.title}</h3>
                    </div>
                </Link>
            </section>
        </section>
    );
};

export default RecentlyCommentedItem;
